import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// Utils
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../../../components/forms/ActionForms/constants';

// Selects
import {
  selectActiveOperatorsAndVehicles,
  selectAssetsAndActivitys,
  selectEditionValues,
} from '../../../../../gql/selectors/actions';
import {
  buildRegisteredByOperators,
  resolveRegisteredBy,
} from '../../../../../utils/registeredByOperators';
import { AuthContextType } from '../../../../../hooks/useAuth';
import { isBasicAction } from '../../../../../utils/order';
import { client } from '../../../../../gql/client';
import { FETCH_USERS } from '../../../../../gql/queries/users';
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';
import { GET_ACTION_BY_ID } from '../../../../../gql/queries/actions/actions';
import { reconcileValidationActionContext } from '../../../../../utils/validationActionContext';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import { ACTION_MEDIA_SCOPE } from '../../../../../utils/draftScopes';
import { preferServerWithCacheFallback } from '../../../../../gql/cacheFallback';
import {
  type BasicSchedule,
  resolveBasicSchedule,
} from '../../../../../components/forms/ActionForms/ActionEdition/basicSchedule';

/**
 * La Order de la actuación, del SERVIDOR (y de la caché solo si no responde a
 * tiempo): el horario de la básica se estampa después de que gestión haya
 * podido leerla (al reanudar, al entregar, con el script), y `cache-first`
 * enseñaba el fin vacío o la hora de alta (revisión final 2026-10-08).
 */
const readOrderPreferServer = (id: string) =>
  preferServerWithCacheFallback(
    client.query({
      query: GET_ORDER_BY_ID,
      variables: { id },
      fetchPolicy: 'network-only',
    }),
    () => {
      const data = client.readQuery({ query: GET_ORDER_BY_ID, variables: { id } });
      return data ? { data } : null;
    }
  );

/**
 * Hora de llegada de la básica (12-oct): vive en la ACTION, no en la Order, y
 * nadie la calcula. Sin la Action (sin red y sin caché), vacía.
 */
const readArrivalOf = async (actionId: string | undefined): Promise<string> => {
  if (!actionId) return '';
  try {
    const { data } = await client.query({
      query: GET_ACTION_BY_ID,
      variables: { id: actionId },
    });
    return data?.action?.registerArrivalDate ?? '';
  } catch {
    return '';
  }
};

const loadFormValues = async (
  userData: AuthContextType | null,
  actionId: string
) => {
  try {
    if (userData?.user) {
      const formValues = await selectEditionValues(actionId);
      return {
        ...formValues,
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Error loading edition data', err);
  }
};

export const EditionValidationLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async ({ params }) => {
  // La URL manda: si el contexto de la pestaña es de OTRA actuación se borra
  // entero y esta carga sale por el `:id` de la URL. Síncrono y antes de
  // cualquier `await` (diagnóstico 2026-10-08 § 8).
  reconcileValidationActionContext(params.id);
  // La lista de fotos y el croquis en curso también son de la actuación
  // de la URL. Las carpetas de OPFS no se tocan: las comparten las
  // pestañas (spec 2026-11-02 wizard-draft-ownership § 6).
  claimDraftScope(ACTION_MEDIA_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Stringify data
  const stringifyForm = sessionStorage.getItem(
    formActionValidationNaming.NEW_VALIDATION_EDITION_FORM
  );

  const stringifyActionData = sessionStorage.getItem(
    formActionNaming.NEW_VALIDATION_ACTION_DATA
  );

  // Variables to assign
  let sessionStorageData;
  let actionData;
  let parentExpeditionOrderStatus;
  let state;
  // Horario de la Actuació bàsica (plan 2026-10-08): inicio y fin de su Order,
  // llegada de su Action (12-oct).
  let basicSchedule: BasicSchedule | null = null;
  // ¿La básica cuelga de un tramo? Sin tramo no hay hueco del que recalcular
  // su horario («Recalcular con el recorrido», 12-oct).
  let isBasicLinkedToSegment = false;
  // Básica por `containerType` o, si falta (Orders anteriores al 10-jul), por
  // el tipo del padre — el mismo respaldo que los loaders hermanos (M1).
  const scheduleOf = (
    order: {
      containerType?: string | null;
      registerInitDate?: string | null;
      registerEndDate?: string | null;
    },
    parentOrderType: string | null | undefined,
    actionId: string | undefined
  ): Promise<BasicSchedule | null> =>
    isBasicAction({ containerType: order.containerType, parentOrderType })
      ? readArrivalOf(actionId).then((registerArrivalDate) => ({
          registerInitDate: order.registerInitDate ?? '',
          registerArrivalDate,
          registerEndDate: order.registerEndDate ?? '',
        }))
      : Promise.resolve(null);
  // Variables assignment
  if (stringifyForm) {
    sessionStorageData = JSON.parse(decompressSessionStorage(stringifyForm));
  }

  if (stringifyActionData) {
    actionData = JSON.parse(decompressSessionStorage(stringifyActionData));
    const {
      data: { order },
    } = await readOrderPreferServer(actionData.orderId);
    const parentOrder = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });
    parentExpeditionOrderStatus = parentOrder.data.order.state;
    state = order.state;
    basicSchedule = await scheduleOf(
      order,
      parentOrder.data.order.type,
      actionData.id
    );
    isBasicLinkedToSegment = !!order.basicActionLinkedSegmentOrderIDs?.length;
  } else if (params.id) {
    // BUG #5 FIX: When navigating via double-click, sessionStorage doesn't have actionData
    const {
      data: { action },
    } = await client.query({
      query: GET_ACTION_BY_ID,
      variables: { id: params.id },
    });

    const {
      data: { order },
    } = await readOrderPreferServer(action.orderIDs[0]);

    actionData = {
      id: params.id,
      orderId: order.id,
      roid: order.roid,
      isActionEdited: 'false',
    };

    const parentOrder = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });
    parentExpeditionOrderStatus = parentOrder.data.order.state;
    state = order.state;
    basicSchedule = await scheduleOf(
      order,
      parentOrder.data.order.type,
      params.id
    );
    isBasicLinkedToSegment = !!order.basicActionLinkedSegmentOrderIDs?.length;
  }

  const dataAssetsAndActivitys = await selectAssetsAndActivitys();
  const loaderToResolve = await loadFormValues(userData, actionData?.id);

  // Opciones del dropdown "Operario que registra" (mismo patrón que
  // NewActionLoader). Se incluye TAMBIÉN en `dataFromServer` para que el diff de
  // AnimalsForm no lo vea como un cambio (es solo la lista de opciones, no un
  // valor editable) y solo dispare el save cuando cambie `registeredByUserId`.
  let registeredByOperators;
  try {
    const { operators } = await selectActiveOperatorsAndVehicles(true);
    registeredByOperators = buildRegisteredByOperators(operators, userData);
  } catch {
    registeredByOperators = buildRegisteredByOperators([], userData);
  }

  // «Registrado por» solo arranca con un valor ELEGIBLE (2026-09-04): una
  // básica guardada con la cuenta de la tablet llega aquí vacía, en rojo, y
  // quien valida elige a la persona. Un operario dado de baja se conserva y
  // se enseña. `dataFromServer` conserva el valor crudo, así que el diff sigue
  // viendo el cambio cuando se elige.
  const knownUsers = (() => {
    try {
      return (
        client.readQuery<{ users?: { id: string; name?: string; surName?: string; rolActive?: string | null }[] }>({
          query: FETCH_USERS,
        })?.users ?? []
      );
    } catch {
      return [];
    }
  })();
  const registeredBy = resolveRegisteredBy(
    sessionStorageData?.registeredByUserId ??
      loaderToResolve?.registeredByUserId,
    registeredByOperators,
    knownUsers
  );

  const combinedData = {
    ...dataAssetsAndActivitys,
    ...loaderToResolve,
    ...sessionStorageData,
    // Después de la sesión: lo que gestión editó manda, pero una sesión
    // rancia no tapa lo que el back estampó después (R1, 12-oct).
    ...(basicSchedule
      ? resolveBasicSchedule(basicSchedule, sessionStorageData, 'dataFromServer')
      : {}),
    ...actionData,
    operators: registeredBy.options,
    registeredByUserId: registeredBy.value,
    parentExpeditionOrderStatus,
    state,
    isBasicAction: !!basicSchedule,
    isBasicLinkedToSegment: !!basicSchedule && isBasicLinkedToSegment,
    // En `dataFromServer` también: el diff de la validación solo manda el
    // horario si gestión lo CAMBIÓ (si no, «no tocar»).
    dataFromServer: {
      ...loaderToResolve,
      ...dataAssetsAndActivitys,
      operators: registeredBy.options,
      ...(basicSchedule ?? {}),
      // También aquí: es un dato de la pantalla, no un cambio que enviar.
      isBasicLinkedToSegment: !!basicSchedule && isBasicLinkedToSegment,
    },
  };

  return combinedData;
};

  return { data: load() };
};
